import { Collapse, styled } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';
import { VyCard } from '../../data-display/VyCard';
import { VySwitchRow } from '../../inputs/VySwitchRow';

/**
 * @tier organism
 *
 * Toggleable section card: a white outer card with a `VySwitchRow` header
 * (title + description on the left, switch on the right) and an animated
 * inset body that reveals when the switch is on. Used in form drawers
 * and dialogs to gate an optional feature configuration (e.g. Discounts,
 * Multiple Installments).
 *
 * The body slot renders inside a `<VyCard variant="inset">` — a darker
 * nested surface — and slides open/closed via MUI `<Collapse>`. The
 * primitive is controlled: the consumer owns `checked` and clears any
 * downstream form data on toggle-off.
 *
 * Net-new primitive (no 1:1 MUI parent), styled via `styled()` reading
 * from theme spacing only — no hex, no inline `sx`.
 */
export type VyToggleSectionProps = {
  /** Title shown next to the switch. */
  title: string;
  /** Optional muted description line under the title. */
  description?: string;
  /** Controlled on/off state of the section's switch. */
  checked: boolean;
  /** Fired when the user toggles the switch. */
  onChange: (next: boolean) => void;
  /** Body rendered inside the inset card when `checked` is true. */
  children: ReactNode;
  /** E2E selector forwarded to the outer card. */
  'data-vy'?: string;
};

const OuterCard = styled(VyCard)(({ theme }) => ({
  paddingInline: theme.spacing(5),
  paddingBlock: theme.spacing(4),
  gap: theme.spacing(2.5),
}));

export const VyToggleSection = forwardRef<HTMLDivElement, VyToggleSectionProps>(
  function VyToggleSection(
    { title, description, checked, onChange, children, 'data-vy': dataVy },
    ref,
  ) {
    return (
      <OuterCard ref={ref} data-vy={dataVy}>
        <VySwitchRow
          title={title}
          description={description}
          checked={checked}
          onChange={(_event, next) => onChange(next)}
        />
        <Collapse in={checked} unmountOnExit>
          <VyCard variant="inset">{children}</VyCard>
        </Collapse>
      </OuterCard>
    );
  },
);
